(() => {
  if (window.__wholenightThemeLoaded) return;
  window.__wholenightThemeLoaded = true;

  const route = (path) =>
    (window.Shopify?.routes?.root || "/") + path;

  const isFrench = () =>
    document.documentElement.lang.toLowerCase().startsWith("fr");

  const text = (en, fr) => isFrench() ? fr : en;

  let cartBusy = false;
  let drawerTrigger = null;
  let previousOverflow = "";
  let toastTimer;

  function notify(message, error = false) {
    let toast = document.querySelector(".shopify-toast");
    if (!toast) {
      toast = document.createElement("div");
      toast.className = "shopify-toast";
      toast.setAttribute("role", "status");
      toast.setAttribute("aria-live", "polite");
      document.body.appendChild(toast);
    }
    toast.textContent = message;
    toast.classList.remove("is-success", "is-error", "is-visible");
    toast.classList.add(error ? "is-error" : "is-success");
    void toast.offsetWidth;
    toast.classList.add("is-visible");
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toast.classList.remove("is-visible"), 4500);
  }

  function openDrawer(trigger) {
    const overlay = document.querySelector(".cart-drawer-overlay");
    if (!overlay) {
      window.location.assign(route("cart"));
      return;
    }
    if (!overlay.classList.contains("is-active")) {
      drawerTrigger = trigger || document.activeElement;
      previousOverflow = document.body.style.overflow;
    }
    overlay.classList.add("is-active");
    overlay.setAttribute("aria-hidden", "false");
    document.body.style.overflow = "hidden";
    overlay.querySelector("[data-cart-drawer-close]")?.focus();
  }

  function closeDrawer() {
    const overlay = document.querySelector(".cart-drawer-overlay");
    if (!overlay?.classList.contains("is-active")) return;
    overlay.classList.remove("is-active");
    overlay.setAttribute("aria-hidden", "true");
    document.body.style.overflow = previousOverflow;
    if (drawerTrigger?.isConnected) drawerTrigger.focus();
  }

  function sectionTargets() {
    return [
      ...document.querySelectorAll(
        "[data-cart-drawer-section], [data-main-cart-section]"
      )
    ].map((element) => ({
      element,
      id: element.dataset.cartDrawerSection ||
          element.dataset.mainCartSection,
      drawer: element.hasAttribute("data-cart-drawer-section")
    }));
  }

  function updateCounts(count) {
    if (!Number.isFinite(count)) return;
    document.querySelectorAll("[data-cart-count]").forEach((element) => {
      element.textContent = count;
      element.style.display = count > 0 ? "" : "none";
    });
    document.querySelectorAll(".cart-count-badge").forEach((element) => {
      element.textContent = count;
    });
  }

  function renderCart(data, targets) {
    let allRendered = true;
    let count = Number.isFinite(data.item_count) ? data.item_count : null;

    for (const target of targets) {
      const html = data.sections?.[target.id];

      if (!html) {
        allRendered = false;
        continue;
      }

      const documentFragment = new DOMParser().parseFromString(html, "text/html");
      const attribute = target.drawer
        ? "data-cart-drawer-section"
        : "data-main-cart-section";

      const replacement = [...documentFragment.querySelectorAll(`[${attribute}]`)]
        .find((node) => node.getAttribute(attribute) === target.id);

      if (!replacement) {
        allRendered = false;
        continue;
      }

      const replacementCount = Number(replacement.dataset.cartItemCount);
      if (Number.isFinite(replacementCount)) count = replacementCount;

      if (target.drawer) {
        const scrollTop = target.element.querySelector(".drawer-body")?.scrollTop || 0;
        const focusedInside = target.element.contains(document.activeElement);

        target.element.innerHTML = replacement.innerHTML;
        target.element.dataset.cartItemCount = replacement.dataset.cartItemCount;

        const body = target.element.querySelector(".drawer-body");
        if (body) body.scrollTop = scrollTop;

        if (focusedInside) {
          target.element.querySelector("[data-cart-drawer-close]")?.focus();
        }
      } else {
        target.element.replaceWith(replacement);
      }
    }

    if (count !== null) updateCounts(count);
    return allRendered;
  }

  async function mutateCart(endpoint, payload, options = {}) {
    if (cartBusy) return;

    const targets = sectionTargets();
    const sectionIds = [...new Set(targets.map((target) => target.id))];

    cartBusy = true;
    document.documentElement.classList.add("wn-cart-busy");

    const trigger = options.trigger;
    const originalDisabled = trigger && "disabled" in trigger
      ? trigger.disabled
      : false;

    if (trigger) {
      trigger.setAttribute("aria-busy", "true");
      if ("disabled" in trigger) trigger.disabled = true;
    }

    try {
      if (payload instanceof FormData) {
        if (sectionIds.length) payload.set("sections", sectionIds.join(","));
        payload.set("sections_url", window.location.pathname);
      } else {
        payload = {
          ...payload,
          ...(sectionIds.length ? { sections: sectionIds } : {}),
          sections_url: window.location.pathname
        };
      }

      const response = await fetch(route(endpoint), {
        method: "POST",
        credentials: "same-origin",
        headers: payload instanceof FormData
          ? { Accept: "application/json" }
          : { Accept: "application/json", "Content-Type": "application/json" },
        body: payload instanceof FormData ? payload : JSON.stringify(payload)
      });

      const data = await response.json();

      if (!response.ok) {
        notify(
          data.description || data.message ||
          text("Could not update the cart.", "Impossible de modifier le panier."),
          true
        );
        return;
      }

      if (!targets.length || !renderCart(data, targets)) {
        window.location.assign(route("cart"));
        return;
      }

      if (options.open) openDrawer(trigger);

      if (endpoint === "cart/add.js") {
        notify(text("Added to your cart.", "Ajouté à votre panier."));
      }
    } catch (error) {
      console.error("Cart request failed:", error);
      notify(
        text(
          "Could not confirm the cart update. Refresh the page to check before trying again.",
          "Impossible de confirmer la mise à jour. Actualisez la page avant de réessayer."
        ),
        true
      );
    } finally {
      cartBusy = false;
      document.documentElement.classList.remove("wn-cart-busy");

      if (trigger?.isConnected) {
        trigger.removeAttribute("aria-busy");
        if ("disabled" in trigger) trigger.disabled = originalDisabled;
      }

      document.querySelectorAll(".main-product-section").forEach(syncProduct);
      document.querySelectorAll("[data-upsell]").forEach(syncUpsell);
    }
  }

  function setMainImage(section, url, label) {
    if (!url) return;

    let image = section.querySelector("#ProductMainMedia");
    if (!image) return;

    if (image.tagName !== "IMG") {
      const replacement = document.createElement("img");
      replacement.id = "ProductMainMedia";
      replacement.className = "rhode-active-image";
      image.replaceWith(replacement);
      image = replacement;
    }

    image.removeAttribute("srcset");
    image.src = url;
    image.alt = label || "";

    const caption = section.querySelector("#ProductImageCaption");
    if (caption && label) caption.textContent = label;

    section.querySelectorAll(".rhode-thumb-btn").forEach((thumb) => {
      thumb.classList.toggle("is-active", thumb.dataset.thumbSrc === url);
    });
  }

  function syncProduct(section) {
    const button = section.querySelector("#AddToCartButton");

    // Bundle products have Color + Language version options. Keep language
    // automatic from the storefront locale and submit the matching combined variant.
    if (section.dataset.autoLanguage === "true") {
      const input = section.querySelector("#ProductVariantId");
      const label = section.querySelector("#SelectedVariantLabel");
      const selectedColor = section.querySelector("[data-auto-color].is-selected");
      const locale = (section.dataset.storefrontLanguage || document.documentElement.lang || "en").toLowerCase();
      const wantsFrench = locale.startsWith("fr");
      const hasColorOption = section.dataset.colorOptionIndex !== "";
      const variantId = hasColorOption
        ? selectedColor?.dataset.variantId
        : section.dataset.initialVariantId;
      const priceText = hasColorOption
        ? selectedColor?.dataset.variantPrice
        : section.dataset.initialVariantPrice;
      const available = hasColorOption
        ? selectedColor?.dataset.variantAvailable === "true"
        : section.dataset.initialVariantAvailable === "true";
      const colorValue = selectedColor?.dataset.colorValue || "";

      section.querySelectorAll("[data-auto-color]").forEach((swatch) => {
        const isSelected = swatch === selectedColor;
        swatch.setAttribute("aria-checked", String(isSelected));
        swatch.classList.toggle("is-selected", isSelected);
      });

      if (input && variantId) input.value = variantId;
      if (label && hasColorOption) label.textContent = colorValue;
      if (priceText) {
        section.querySelectorAll("[data-product-price]").forEach((price) => {
          price.textContent = "— " + priceText;
        });
      }

      const featuredImage = selectedColor?.dataset.variantImage;
      if (featuredImage) setMainImage(section, featuredImage, colorValue);

      if (button) {
        button.disabled = !available || cartBusy;
        const labelElement = button.querySelector("[data-add-label-text]");
        let soldOutText = button.dataset.soldOutLabel;
        if (!soldOutText || soldOutText.includes("translation missing")) {
          soldOutText = wantsFrench ? "Épuisé" : "Sold out";
        }
        if (labelElement) labelElement.textContent = available ? button.dataset.addLabel : soldOutText;
      }
      return;
    }

    const selected = section.querySelector(".rhode-swatch-btn.is-selected");
    if (selected) {
      const input = section.querySelector("#ProductVariantId");
      const label = section.querySelector("#SelectedVariantLabel");

      if (input) input.value = selected.dataset.variantId;
      if (label) label.textContent = selected.dataset.variantTitle || "";

      section.querySelectorAll("[data-product-price]").forEach((price) => {
        price.textContent = "— " + selected.dataset.variantPrice;
      });

      section.querySelectorAll(".rhode-swatch-btn").forEach((swatch) => {
        swatch.setAttribute("aria-checked", String(swatch === selected));
      });

      if (button) {
        const available = selected.dataset.variantAvailable === "true";
        button.disabled = !available || cartBusy;
        const labelElement = button.querySelector("[data-add-label-text]");
        let soldOutText = button.dataset.soldOutLabel;
        if (!soldOutText || soldOutText.includes('translation missing')) {
          soldOutText = isFrench() ? 'Épuisé' : 'Sold out';
        }
        if (labelElement) {
          labelElement.textContent = available ? button.dataset.addLabel : soldOutText;
        }
      }
    }
  }

  function syncPackaging(section, card, changeImage = false) {
    if (!card) return;
    section.querySelectorAll(".packaging-card").forEach((other) => {
      other.classList.toggle("is-selected", other === card);
      other.setAttribute("aria-checked", String(other === card));
    });

    const input = section.querySelector("#ProductPackagingInput");
    const label = section.querySelector("#SelectedPackagingLabel");

    if (input) input.value = card.dataset.packagingValue || "";
    if (label) {
      label.textContent =
        `${card.dataset.packagingLabel || ""} (${section.dataset.freeLabel || "Free"})`;
    }

    if (changeImage && card.dataset.packagingImg) {
      setMainImage(section, card.dataset.packagingImg, card.dataset.packagingLabel);
    }
  }

  function syncUpsell(card) {
    const select = card.querySelector("[data-upsell-variant]");
    if (!select) return;

    const option = select.selectedOptions[0];
    if (!option) return;

    const price = card.querySelector("[data-upsell-price]");
    const compare = card.querySelector("[data-upsell-compare]");
    const saving = card.querySelector("[data-upsell-saving]");
    const button = card.querySelector("[data-ajax-add]");

    if (price) price.textContent = option.dataset.price || "";
    if (compare) {
      compare.textContent = option.dataset.compare || "";
      compare.hidden = !option.dataset.compare;
    }
    if (saving) {
      saving.textContent = option.dataset.saving || "";
      saving.hidden = !option.dataset.saving;
    }
    if (button) {
      const available = option.dataset.available === "true";
      button.dataset.variantId = option.value;
      button.disabled = !available || cartBusy;
      button.textContent = available
        ? button.dataset.addLabel
        : button.dataset.soldLabel;
    }
  }

  function closeLocales(except = null) {
    document.querySelectorAll("[data-locale-dropdown]").forEach((wrap) => {
      if (wrap === except) return;
      wrap.classList.remove("is-open");
      wrap.querySelector("[data-locale-trigger]")?.setAttribute("aria-expanded", "false");
      const menu = wrap.querySelector(".locale-menu");
      if (menu) menu.hidden = true;
    });
  }

  let mobileOverflow = "";

  function closeMobileMenu() {
    const menu = document.getElementById("MobileMenuLayer");
    if (!menu || menu.style.display !== "flex") return;
    menu.style.display = "none";
    document.body.style.overflow = mobileOverflow;
  }

  document.addEventListener("submit", (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    if (event.submitter?.name === "checkout") {
      if (cartBusy) event.preventDefault();
      return;
    }

    if (!new URL(form.action, window.location.href).pathname.endsWith("/cart/add")) return;

    event.preventDefault();
    if (cartBusy) return;

    const data = new FormData(form);
    const variantId = data.get("id");
    const quantity = Number(data.get("quantity") || 1);

    if (!variantId || !Number.isInteger(quantity) || quantity < 1) {
      notify(text("Select a product and a valid quantity.", "Choisissez un produit et une quantité valide."), true);
      return;
    }

    const button = form.querySelector('[type="submit"]');
    if (button?.disabled) return;

    mutateCart("cart/add.js", data, { open: true, trigger: button });
  });

  document.addEventListener("change", (event) => {
    if (event.target.matches("[data-upsell-variant]")) {
      syncUpsell(event.target.closest("[data-upsell]"));
    }

    if (event.target.matches("#ProductQuantity")) {
      const value = Math.floor(Number(event.target.value));
      event.target.value = Number.isFinite(value) && value >= 1 ? value : 1;
    }
  });

  document.addEventListener("click", (event) => {
    if (!(event.target instanceof Element)) return;
    const target = event.target;

    const change = target.closest("[data-cart-change]");
    if (change) {
      if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      if (cartBusy) return;
      const quantity = Number(change.dataset.quantity);
      if (!Number.isInteger(quantity) || quantity < 0) return;

      mutateCart("cart/change.js", {
        id: change.dataset.key,
        quantity
      }, { trigger: change });
      return;
    }

    const add = target.closest("[data-ajax-add]");
    if (add) {
      event.preventDefault();
      if (cartBusy || add.disabled) return;

      const id = add.dataset.variantId;
      if (!id) return;

      mutateCart("cart/add.js", {
        items: [{ id, quantity: 1 }]
      }, { open: true, trigger: add });
      return;
    }

    const open = target.closest("[data-cart-drawer-open]");
    if (open) {
      event.preventDefault();
      openDrawer(open);
      return;
    }

    if (target.closest("[data-cart-drawer-close]") ||
        target.matches(".cart-drawer-overlay")) {
      event.preventDefault();
      closeDrawer();
      return;
    }

    const section = target.closest(".main-product-section");

    if (section) {
      const swatch = target.closest(".rhode-swatch-btn");
      if (swatch) {
        event.preventDefault();
        section.querySelectorAll(".rhode-swatch-btn").forEach((button) => {
          button.classList.toggle("is-selected", button === swatch);
        });
        syncProduct(section);
        if (section.dataset.autoLanguage === "true") {
          const selectedVariantId = section.querySelector("#ProductVariantId")?.value;
          const url = new URL(window.location.href);
          if (selectedVariantId) url.searchParams.set("variant", selectedVariantId);
          window.history.replaceState({}, "", url);
        } else {
          setMainImage(section, swatch.dataset.variantImage, swatch.dataset.variantTitle);
          const url = new URL(window.location.href);
          url.searchParams.set("variant", swatch.dataset.variantId);
          window.history.replaceState({}, "", url);
        }
        return;
      }

      const thumbnail = target.closest(".rhode-thumb-btn");
      if (thumbnail) {
        setMainImage(section, thumbnail.dataset.thumbSrc, thumbnail.dataset.thumbLabel);
        return;
      }

      const packaging = target.closest(".packaging-card");
      if (packaging) {
        syncPackaging(section, packaging, true);
        return;
      }

      const quantityButton = target.closest("[data-qty-decrease], [data-qty-increase]");
      if (quantityButton) {
        const input = section.querySelector("#ProductQuantity");
        if (input) {
          const current = Math.floor(Number(input.value)) || 1;
          input.value = Math.max(1, current + (quantityButton.hasAttribute("data-qty-increase") ? 1 : -1));
        }
        return;
      }
    }

    const accordion = target.closest(".rhode-accordion-header, .rhode-accordion-trigger");
    if (accordion) {
      const item = accordion.closest(".rhode-accordion-item");
      if (item) {
        const open = item.classList.toggle("is-open");
        accordion.setAttribute("aria-expanded", String(open));
      }
      return;
    }

    if (target.closest("[data-mobile-menu-open]")) {
      const menu = document.getElementById("MobileMenuLayer");
      if (menu && menu.style.display !== "flex") {
        mobileOverflow = document.body.style.overflow;
        menu.style.display = "flex";
        document.body.style.overflow = "hidden";
      }
      return;
    }

    if (target.closest("[data-mobile-menu-close]")) {
      closeMobileMenu();
      return;
    }

    const localeValue = target.closest("[data-locale-value]");
    if (localeValue) {
      event.preventDefault();
      const wrap = localeValue.closest("[data-locale-dropdown]");
      const form = wrap?.closest("form");
      const input = [...(form?.querySelectorAll("[data-locale-input]") || [])]
        .find((node) => node.dataset.localeInput === localeValue.dataset.localeName);
      if (input && form) {
        input.value = localeValue.dataset.localeValue;
        HTMLFormElement.prototype.submit.call(form);
      }
      return;
    }

    const localeTrigger = target.closest("[data-locale-trigger]");
    if (localeTrigger) {
      event.preventDefault();
      const wrap = localeTrigger.closest("[data-locale-dropdown]");
      const menu = wrap?.querySelector(".locale-menu");
      if (!wrap || !menu) return;

      closeLocales(wrap);
      const opened = wrap.classList.toggle("is-open");
      localeTrigger.setAttribute("aria-expanded", String(opened));
      menu.hidden = !opened;
      const opensUp = window.innerHeight - localeTrigger.getBoundingClientRect().bottom < 280;
      wrap.classList.toggle("opens-up", opensUp);
      wrap.classList.toggle("opens-down", !opensUp);
      return;
    }

    if (!target.closest("[data-locale-dropdown]")) closeLocales();
  });

  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape") {
      closeDrawer();
      closeMobileMenu();
      closeLocales();
    }

    const overlay = document.querySelector(".cart-drawer-overlay.is-active");
    if (event.key !== "Tab" || !overlay) return;

    const focusable = [...overlay.querySelectorAll(
      'a[href],button:not([disabled]),select:not([disabled]),input:not([disabled]),[tabindex="0"]'
    )].filter((element) => element.getClientRects().length);

    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  function initialize(root = document) {
    root.querySelectorAll(".main-product-section").forEach((section) => {
      syncProduct(section);
      syncPackaging(section, section.querySelector(".packaging-card.is-selected"));
    });

    root.querySelectorAll("[data-upsell]").forEach(syncUpsell);

    const oldPopup = document.getElementById("CrossSellPopup");
    if (oldPopup) oldPopup.hidden = true;

    const header = document.querySelector(".header");
    header?.classList.toggle("is-scrolled", window.scrollY > 12);
  }

  document.addEventListener("shopify:section:load", (event) => initialize(event.target));
  window.addEventListener("resize", () => {
    if (window.innerWidth > 1024) closeMobileMenu();
  });
  window.addEventListener("scroll", () => {
    document.querySelector(".header")?.classList.toggle("is-scrolled", window.scrollY > 12);
  }, { passive: true });

  const style = document.createElement("style");
  style.textContent = `
    #CrossSellPopup { display:none!important; }
    .wn-cart-busy [data-cart-change] { opacity:.45; cursor:wait; }
    [data-ajax-add]:disabled { opacity:.5; cursor:not-allowed; }
  `;
  document.head.appendChild(style);

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initialize(), { once: true });
  } else {
    initialize();
  }
})();